// Prompt-var data layer (#52) — the UI half of `/v1/prompt-vars` + the MCP
// `config` verb's KV actions.
//
// These are the values that resolve `#{{key}}` placeholders in the org/template
// prompts and render as the boot prompt's compact `[scope] key: value` block.
// Scopes `account | repo | template`, general to specific — the more specific
// value wins a key clash. `read:runners` reads, `write:runners` writes.
//
// THE ADDRESS IS `(scope, ref, key)` and it is split path/query exactly as the
// route splits it: `key` has a safe path charset, a `ref` is a repo id or a
// template NAME and may contain a slash, so it rides the query string where no
// segment can split it. `account` scope takes NO ref — the API refuses one (400),
// and this client sends none rather than an empty string.
//
// NO CURSOR, deliberately — same reasoning as `agent-templates.ts`: the route
// answers the canonical `{data, next_cursor}` envelope with `next_cursor` ALWAYS
// null, because it serves the whole (optionally narrowed) collection — "a
// silently truncated KV is a config file with lines missing". So there is no page
// two to reach and no Load-more to offer.
//
// NEVER A CREDENTIAL: `prompt_vars` is plaintext jsonb interpolated into prompt
// text. A secret-shaped value is refused server-side (422) by the ONE detector
// both doors call; this client does not re-implement it — it renders the refusal.

import { type PromptVarScope, PromptVarScopeSchema } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import type { Page } from './paged';
import { SESSION_TOKEN } from './session';

export type { PromptVarScope };

/**
 * The scopes the picker offers, general to specific — read off the DOMAIN enum
 * the API parses `:scope` with, never a second list. A hardcoded copy here is
 * how a form ends up offering a scope the route 400s on, or hiding one it serves.
 */
export const PROMPT_VAR_SCOPES: readonly PromptVarScope[] = PromptVarScopeSchema.options;

/**
 * One prompt var as `/v1/prompt-vars` serves it — the API's `PromptVarView`
 * (`packages/mcp/src/army/config.ts`), snake_case, account-scope `ref` as null.
 * `value` is arbitrary JSON and passes through verbatim on both surfaces.
 */
export type ApiPromptVar = {
  readonly id: string;
  readonly scope: PromptVarScope;
  readonly ref: string | null;
  readonly key: string;
  readonly value: unknown;
  readonly created_iso: string;
  readonly updated_iso: string;
};

/** The address of one var. `ref` is `null` for account scope. */
export type PromptVarAddress = {
  readonly scope: PromptVarScope;
  readonly ref: string | null;
  readonly key: string;
};

export const PROMPT_VARS_QUERY_KEY = 'prompt_vars';

/** `?scope=&ref=` — the list narrowing the route advertises, both optional. */
export type PromptVarFilter = {
  readonly scope?: PromptVarScope | undefined;
  readonly ref?: string | undefined;
};

/** The filter as a query string (leading `?`), or `''` when nothing is narrowed. */
function filterQuery(filter: PromptVarFilter): string {
  const params = new URLSearchParams();
  if (filter.scope) params.set('scope', filter.scope);
  if (filter.ref) params.set('ref', filter.ref);
  const qs = params.toString();
  return qs === '' ? '' : `?${qs}`;
}

/** The path of one addressed var, ref in the query where a slash can't split it. */
function varPath(at: PromptVarAddress): string {
  const base = `/v1/prompt-vars/${encodeURIComponent(at.scope)}/${encodeURIComponent(at.key)}`;
  return at.ref === null || at.ref === '' ? base : `${base}?ref=${encodeURIComponent(at.ref)}`;
}

/**
 * The account's prompt vars, optionally narrowed. The filter is part of the
 * query KEY, so narrowing re-reads server-side rather than filtering rows the
 * client happens to hold — the list is not exhaustively loaded here.
 */
export function usePromptVars(filter: () => PromptVarFilter, token: string | null = SESSION_TOKEN) {
  return createQuery<readonly ApiPromptVar[]>(() => ({
    queryKey: [
      PROMPT_VARS_QUERY_KEY,
      filter().scope ?? '',
      filter().ref ?? '',
      token,
      activeAccountId(),
    ],
    enabled: token !== null,
    queryFn: async () =>
      (await apiFetch<Page<ApiPromptVar>>(`/v1/prompt-vars${filterQuery(filter())}`, token)).data,
  }));
}

/** Set one var. Idempotent upsert by `(scope, ref, key)`, like the MCP action. */
export async function setPromptVar(
  at: PromptVarAddress,
  value: unknown,
  token: string | null = SESSION_TOKEN,
): Promise<ApiPromptVar> {
  return (
    await apiFetch<{ var: ApiPromptVar }>(varPath(at), token, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    })
  ).var;
}

/** Clear one var. Idempotent server-side (204 whether or not it was set). */
export async function deletePromptVar(
  at: PromptVarAddress,
  token: string | null = SESSION_TOKEN,
): Promise<void> {
  await apiFetch<void>(varPath(at), token, { method: 'DELETE' });
}
